import React, { useCallback, useEffect, useRef, useState } from 'react';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import '../../assets/styles/video.css';
import { Box, Button, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Video } from '../../api/interfaces/Video';
import { profile } from '../../api/services/user';
import { handleApiError } from '../../utils/errorHandler';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { getVideoById, markVideoAsRecentlyWatched } from '../../api/services/video';

const WatchRumbleVideoPlayerPage: React.FC = () => {
    const navigate = useNavigate();
    const { videoId } = useParams();

    const [video, setVideo] = useState<Video | null>(null);
    const [watermarkCurrentEmail, setWatermarkCurrentEmail] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const outerContainerRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);


    const fetchProfile = useCallback(async () => {
        try {
            const response = await profile();
            const user = response.data.user;
            setWatermarkCurrentEmail(user.email ?? '');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchVideo = useCallback(async () => {
        try {
            if (!videoId) throw new Error('Video ID is missing');
            const response = await getVideoById(videoId);
            setVideo(response.data.video);
        } catch (error) {
            console.error(error);
        }
    }, [videoId]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await Promise.all([fetchProfile(), fetchVideo()]);
            setIsLoading(false);

            // Mark video as recently watched in background
            markVideoAsRecentlyWatched(videoId as string);
        };
        init();
    }, [fetchProfile, fetchVideo, videoId]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            if (outerContainerRef.current?.requestFullscreen) {
                outerContainerRef.current.requestFullscreen();
                setIsFullscreen(true);
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
                setIsFullscreen(false);
            }
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div
            ref={outerContainerRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                margin: 0,
                padding: 0,
                zIndex: 9999,
                backgroundColor: 'black',
            }}
        >
            {isLoading ? (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                    }}
                >
                    <CircularProgress size={100} sx={{ color: "#fff" }} />
                </div>
            ) : (
                <>
                    <div className="watermark-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <iframe
                            style={{
                                width: '100%',
                                height: '100%',
                                position: 'relative',
                                border: 'none'
                            }}
                            src={video?.videoUrl}
                            title="Rumble Video Player"
                            allowFullScreen={false}
                        ></iframe>
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 50,
                                right: 10,
                                color: 'white',
                                fontSize: '16px',
                                fontWeight: 600,
                                opacity: 0.7,
                                pointerEvents: 'none',
                                userSelect: 'none',
                            }}
                        >
                            {watermarkCurrentEmail}
                        </Box>
                        <div className="watermark-overlay">
                            {Array.from({ length: 20 }).map((_, rowIndex) => (
                                <div
                                    key={rowIndex}
                                    style={{
                                        display: 'flex',
                                        justifyContent: rowIndex % 2 === 0 ? 'flex-start' : 'flex-end',
                                        width: '100%',
                                    }}
                                >
                                    {Array.from({ length: 10 }).map((_, colIndex) => (
                                        <span key={colIndex} style={{ padding: '0 8px' }}>
                                            {watermarkCurrentEmail}
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {!isFullscreen && (
                        <Button
                            variant="contained"
                            onClick={handleBack}
                            style={{
                                position: 'fixed',
                                top: 10,
                                left: 10,
                                zIndex: 20,
                                padding: '8px 12px',
                                fontSize: 16,
                                background: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                            }}
                            aria-label="Back"
                        >
                            <ArrowBackIosNewOutlinedIcon />
                        </Button>
                    )}

                    {/* Fullscreen toggle top-right */}
                    <Button
                        variant="contained"
                        onClick={toggleFullscreen}
                        style={{
                            position: 'fixed',
                            top: 10,
                            right: 10,
                            zIndex: 20,
                            padding: '8px 12px',
                            fontSize: 16,
                            background: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                        }}
                        aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                    >
                        {isFullscreen ? <FullscreenExitOutlinedIcon /> : <FullscreenOutlinedIcon />}
                    </Button>
                </>
            )}




        </div>
    );
};

export default WatchRumbleVideoPlayerPage;
